<script>
import { setTimeLocale } from 'shared/helpers/timeHelper';
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    StatusBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = locale;
        setTimeLocale(locale);
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
    :data-account-id="currentAccountId"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <StatusBanner />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>

<style>
/* === Per-account theming: Suritec (account_id=3) === */
/* Design system: dark Suritec applied as cohesive layered scheme */
[data-account-id="3"] {
  /* Foundation tokens */
  --suritec-black: 13, 13, 15;          /* #0D0D0F  layer 0 */
  --suritec-surface: 24, 23, 29;        /* #18171D  layer 1 */
  --suritec-surface-hover: 33, 32, 42;  /* #21202A  layer 1 hover */
  --suritec-surface-active: 42, 41, 51; /* #2A2933  layer 2 */
  --suritec-line: 45, 44, 54;           /* #2D2C36  borders */
  --suritec-line-strong: 62, 60, 74;    /* #3E3C4A  emphasized borders */
  --suritec-text: 244, 242, 238;        /* #F4F2EE  body */
  --suritec-text-strong: 255, 255, 255; /* #FFFFFF  headings */
  --suritec-muted: 157, 154, 163;       /* #9D9AA3  secondary */
  --suritec-orange: 244, 151, 26;       /* #F4971A  accent */
  --suritec-orange-hover: 219, 135, 26; /* #DB871A  accent hover */
  --suritec-orange-soft: 30, 22, 11;    /* deep tinted bg for orange-on-dark */
  --suritec-orange-on: 31, 20, 8;       /* #1F1408  text on orange */

  background-color: rgb(var(--suritec-black));
  color: rgb(var(--suritec-text));
}

/* ─── Layer 0: app & sidebar ─── */
[data-account-id="3"] .bg-n-background,
[data-account-id="3"] .bg-n-solid-1,
[data-account-id="3"] .bg-n-solid-2,
[data-account-id="3"] .bg-n-surface-1 {
  background-color: rgb(var(--suritec-black)) !important;
}

/* ─── Layer 1: surfaces (lists, cards, conversation list, message panel) ─── */
[data-account-id="3"] .bg-n-solid-3,
[data-account-id="3"] .bg-n-surface-2,
[data-account-id="3"] .bg-n-surface-3,
[data-account-id="3"] .bg-n-slate-2,
[data-account-id="3"] .bg-n-slate-3,
[data-account-id="3"] .bg-white {
  background-color: rgb(var(--suritec-surface)) !important;
}

/* ─── Layer 2: hover / active states ─── */
[data-account-id="3"] [class*="hover:bg-n-slate"]:hover,
[data-account-id="3"] [class*="hover:bg-n-background"]:hover,
[data-account-id="3"] [class*="hover:bg-n-alpha"]:hover {
  background-color: rgb(var(--suritec-surface-hover)) !important;
}
[data-account-id="3"] .bg-n-alpha-1,
[data-account-id="3"] .bg-n-alpha-2 {
  background-color: rgba(var(--suritec-orange), 0.08) !important;
}

/* ─── Borders ─── */
[data-account-id="3"] .border-n-slate-3,
[data-account-id="3"] .border-n-slate-4,
[data-account-id="3"] .border-n-slate-5,
[data-account-id="3"] .border-n-weak,
[data-account-id="3"] [class*="border-l-n-slate"],
[data-account-id="3"] [class*="border-r-n-slate"],
[data-account-id="3"] [class*="border-t-n-slate"],
[data-account-id="3"] [class*="border-b-n-slate"] {
  border-color: rgb(var(--suritec-line)) !important;
}

/* ─── Text ─── */
[data-account-id="3"] .text-n-slate-12 {
  color: rgb(var(--suritec-text-strong)) !important;
}
[data-account-id="3"] .text-n-slate-11 {
  color: rgb(var(--suritec-text)) !important;
}
[data-account-id="3"] .text-n-slate-10,
[data-account-id="3"] .text-n-slate-9 {
  color: rgb(var(--suritec-muted)) !important;
}

/* ─── Orange accents (tabs, links, brand bar, badges) ─── */
[data-account-id="3"] .after\:bg-n-brand::after,
[data-account-id="3"] .bg-n-brand {
  background-color: rgb(var(--suritec-orange)) !important;
}
[data-account-id="3"] .text-n-blue-9,
[data-account-id="3"] .text-n-blue-10,
[data-account-id="3"] .text-n-blue-11,
[data-account-id="3"] .text-n-blue-12 {
  color: rgb(var(--suritec-orange)) !important;
}
[data-account-id="3"] .bg-n-blue-1,
[data-account-id="3"] .bg-n-blue-2,
[data-account-id="3"] .bg-n-blue-3 {
  background-color: rgba(var(--suritec-orange), 0.15) !important;
}
[data-account-id="3"] .bg-n-blue-9,
[data-account-id="3"] .bg-n-blue-10,
[data-account-id="3"] .bg-n-blue-11 {
  background-color: rgb(var(--suritec-orange)) !important;
  color: rgb(var(--suritec-orange-on)) !important;
}
[data-account-id="3"] .border-n-blue-3,
[data-account-id="3"] .border-n-blue-4,
[data-account-id="3"] .border-n-blue-9,
[data-account-id="3"] .border-n-blue-11 {
  border-color: rgb(var(--suritec-orange)) !important;
}
[data-account-id="3"] .bg-n-solid-blue {
  background-color: rgba(var(--suritec-orange), 0.15) !important;
}

/* ─── woot primary buttons (Senden, etc.) ─── */
[data-account-id="3"] .bg-woot-500,
[data-account-id="3"] .bg-woot-600 {
  background-color: rgb(var(--suritec-orange)) !important;
  color: rgb(var(--suritec-orange-on)) !important;
}
[data-account-id="3"] .hover\:bg-woot-600:hover,
[data-account-id="3"] .hover\:bg-woot-700:hover {
  background-color: rgb(var(--suritec-orange-hover)) !important;
}
[data-account-id="3"] .text-woot-500,
[data-account-id="3"] .text-woot-600 {
  color: rgb(var(--suritec-orange)) !important;
}
[data-account-id="3"] .border-woot-500 {
  border-color: rgb(var(--suritec-orange)) !important;
}

/* ─── Message bubbles inside conversation view ─── */
/* Agent (incoming, from agent or bot) — dark surface bubble with white text */
[data-account-id="3"] .chat-bubble.agent,
[data-account-id="3"] .chat-bubble.has-attachment.agent,
[data-account-id="3"] [class*="bg-n-background"][class*="bubble"] {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
}
/* User (outgoing, visitor / customer in chatwoot inbox) — orange solid */
[data-account-id="3"] .chat-bubble.user,
[data-account-id="3"] .conv-message__user,
[data-account-id="3"] .right .chat-bubble {
  background-color: rgb(var(--suritec-orange)) !important;
  color: rgb(var(--suritec-orange-on)) !important;
  border: 0 !important;
}
/* Private notes — soft amber */
[data-account-id="3"] .chat-bubble.private {
  background-color: rgba(var(--suritec-orange), 0.10) !important;
  border-color: rgba(var(--suritec-orange), 0.35) !important;
}

/* ─── Composer (reply input area) ─── */
[data-account-id="3"] .reply-box,
[data-account-id="3"] .conversation--reply,
[data-account-id="3"] [class*="reply-box"] {
  background-color: rgb(var(--suritec-surface)) !important;
  border-top: 1px solid rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] textarea,
[data-account-id="3"] input[type="text"],
[data-account-id="3"] input[type="email"],
[data-account-id="3"] input[type="search"] {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border-color: rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] textarea::placeholder,
[data-account-id="3"] input::placeholder {
  color: rgb(var(--suritec-muted)) !important;
}
[data-account-id="3"] textarea:focus,
[data-account-id="3"] input:focus {
  border-color: rgb(var(--suritec-orange)) !important;
  box-shadow: 0 0 0 3px rgba(var(--suritec-orange), 0.18) !important;
  outline: none !important;
}

/* ─── Conversation list items ─── */
[data-account-id="3"] .conversation--item,
[data-account-id="3"] [class*="conversation--item"] {
  background-color: rgb(var(--suritec-black)) !important;
  border-bottom: 1px solid rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] .conversation--item:hover,
[data-account-id="3"] [class*="conversation--item"]:hover {
  background-color: rgb(var(--suritec-surface)) !important;
}
[data-account-id="3"] .conversation--item.active,
[data-account-id="3"] [class*="conversation--item"].active,
[data-account-id="3"] [class*="conversation--item"][class*="active"] {
  background-color: rgb(var(--suritec-surface-active)) !important;
  border-left: 3px solid rgb(var(--suritec-orange)) !important;
}

/* ─── kbd keyboard shortcuts (empty state) ─── */
[data-account-id="3"] kbd,
[data-account-id="3"] .bg-n-alpha-1 kbd,
[data-account-id="3"] [class*="kbd"],
[data-account-id="3"] code {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
}

/* ─── Sidebar refinement ─── */
[data-account-id="3"] aside {
  background-color: rgb(var(--suritec-black)) !important;
  border-right: 1px solid rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] aside [class*="active"],
[data-account-id="3"] aside [aria-current="page"] {
  background-color: rgba(var(--suritec-orange), 0.10) !important;
  color: rgb(var(--suritec-orange)) !important;
}

/* ─── Top conversation header strip ─── */
[data-account-id="3"] .conversation-details-wrap > header,
[data-account-id="3"] [class*="conv-header"] {
  background-color: rgb(var(--suritec-surface)) !important;
  border-bottom: 1px solid rgb(var(--suritec-line)) !important;
}

/* ─── Status banners (assignment notice etc.) ─── */
[data-account-id="3"] [class*="banner"] {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-muted)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
}

/* ─── Avatars: muted backgrounds when light palette assigned ─── */
[data-account-id="3"] [class*="avatar"] {
  border: 1px solid rgb(var(--suritec-line)) !important;
}

/* ─── Bubble content text MUST be readable (override accent colors inside bubbles) ─── */
[data-account-id="3"] .chat-bubble,
[data-account-id="3"] .chat-bubble *,
[data-account-id="3"] .agent-message-wrap .message-text,
[data-account-id="3"] .agent-message-wrap .message-text *,
[data-account-id="3"] .user-message-wrap .message-text,
[data-account-id="3"] .user-message-wrap .message-text * {
  color: inherit !important;
}
[data-account-id="3"] .chat-bubble.agent,
[data-account-id="3"] .chat-bubble.agent * {
  color: rgb(var(--suritec-text)) !important;
}
[data-account-id="3"] .chat-bubble.user,
[data-account-id="3"] .chat-bubble.user * {
  color: rgb(var(--suritec-orange-on)) !important;
}
/* But preserve orange for the citation block / "Quellen" italics */
[data-account-id="3"] .chat-bubble.agent em,
[data-account-id="3"] .chat-bubble.agent i {
  color: rgb(var(--suritec-muted)) !important;
}
/* Stronger headings within bubbles */
[data-account-id="3"] .chat-bubble.agent strong,
[data-account-id="3"] .chat-bubble.agent b,
[data-account-id="3"] .chat-bubble.agent h1,
[data-account-id="3"] .chat-bubble.agent h2,
[data-account-id="3"] .chat-bubble.agent h3 {
  color: rgb(var(--suritec-text-strong)) !important;
  font-weight: 600;
}
/* Bullet list markers in orange */
[data-account-id="3"] .chat-bubble.agent ul li::marker,
[data-account-id="3"] .chat-bubble.agent ol li::marker {
  color: rgb(var(--suritec-orange)) !important;
}
/* Links inside bubbles stay accented but underlined for clarity */
[data-account-id="3"] .chat-bubble a {
  color: rgb(var(--suritec-orange)) !important;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ─── Top-right action icons (sort/filter) ─── */
[data-account-id="3"] button[class*="bg-white"],
[data-account-id="3"] .bg-n-alpha-2 {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] button:hover[class*="bg-white"] {
  background-color: rgb(var(--suritec-surface-hover)) !important;
}

/* ─── Improve avatar contrast inside the dark scope ─── */
[data-account-id="3"] [class*="avatar"] span {
  background-color: rgb(var(--suritec-surface-active)) !important;
  color: rgb(var(--suritec-orange)) !important;
}

/* ─── Reply box (composer) refinements ─── */
[data-account-id="3"] .ProseMirror,
[data-account-id="3"] [contenteditable="true"] {
  color: rgb(var(--suritec-text)) !important;
  background-color: transparent !important;
}
[data-account-id="3"] .ProseMirror p.is-editor-empty:first-child::before {
  color: rgb(var(--suritec-muted)) !important;
}


/* ─── iter10: Force readable text on actual Chatwoot dashboard bubbles ─── */
[data-account-id="3"] .right-bubble,
[data-account-id="3"] .left-bubble,
[data-account-id="3"] [class*="right-bubble"],
[data-account-id="3"] [class*="left-bubble"] {
  background-color: rgb(var(--suritec-surface)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
  color: rgb(var(--suritec-text)) !important;
}
[data-account-id="3"] .right-bubble *,
[data-account-id="3"] .left-bubble *,
[data-account-id="3"] [class*="right-bubble"] *,
[data-account-id="3"] [class*="left-bubble"] * {
  color: rgb(var(--suritec-text)) !important;
}
[data-account-id="3"] .prose,
[data-account-id="3"] .prose *,
[data-account-id="3"] .prose-bubble,
[data-account-id="3"] .prose-bubble * {
  color: rgb(var(--suritec-text)) !important;
}
[data-account-id="3"] .prose strong,
[data-account-id="3"] .prose b,
[data-account-id="3"] .prose h1,
[data-account-id="3"] .prose h2,
[data-account-id="3"] .prose h3,
[data-account-id="3"] .prose-bubble strong,
[data-account-id="3"] .prose-bubble b {
  color: rgb(var(--suritec-text-strong)) !important;
}
[data-account-id="3"] .prose a,
[data-account-id="3"] .prose-bubble a {
  color: rgb(var(--suritec-orange)) !important;
  text-decoration: underline;
}
[data-account-id="3"] .prose em,
[data-account-id="3"] .prose-bubble em,
[data-account-id="3"] .prose i,
[data-account-id="3"] .prose-bubble i {
  color: rgb(var(--suritec-muted)) !important;
}
[data-account-id="3"] .prose ul li::marker,
[data-account-id="3"] .prose ol li::marker,
[data-account-id="3"] .prose-bubble ul li::marker,
[data-account-id="3"] .prose-bubble ol li::marker {
  color: rgb(var(--suritec-orange)) !important;
}

/* Conversation cards in list — make name + snippet readable */
[data-account-id="3"] .conversations-list-wrap [class*="conversation"] h4,
[data-account-id="3"] .conversations-list-wrap [class*="conversation"] h5,
[data-account-id="3"] .conversations-list-wrap [class*="conversation"] [class*="title"] {
  color: rgb(var(--suritec-text-strong)) !important;
}
[data-account-id="3"] .conversations-list-wrap [class*="conversation"] p,
[data-account-id="3"] .conversations-list-wrap [class*="conversation"] [class*="snippet"],
[data-account-id="3"] .conversations-list-wrap [class*="conversation"] [class*="preview"] {
  color: rgb(var(--suritec-muted)) !important;
}

/* Right side bubble = bot/agent outgoing in dashboard — keep dark surface for readability */
[data-account-id="3"] .right-bubble.bg-n-blue-3,
[data-account-id="3"] [class*="right-bubble"][class*="bg-n-blue"] {
  background-color: rgb(var(--suritec-surface)) !important;
}
/* Left bubble = customer incoming — accent orange */
[data-account-id="3"] .left-bubble[class*="bg-n-"] {
  background-color: rgba(var(--suritec-orange), 0.12) !important;
  border-color: rgba(var(--suritec-orange), 0.35) !important;
}


/* ─── iter12: account switcher, gradient cleanup, bg-white override ─── */

/* Hide white-to-transparent gradient at sidebar bottom (the visual "fade" Max called degradado) */
[data-account-id="3"] .bg-gradient-to-t[class*="from-n-background"],
[data-account-id="3"] [class*="from-white"][class*="to-transparent"] {
  background: none !important;
  display: none !important;
}

/* Account switcher dialog/popover */
[data-account-id="3"] [class*="SidebarAccountSwitcher"],
[data-account-id="3"] [class*="account-switcher"],
[data-account-id="3"] [role="menu"],
[data-account-id="3"] [role="dialog"],
[data-account-id="3"] [role="listbox"],
[data-account-id="3"] [class*="Popper"],
[data-account-id="3"] [class*="popover"],
[data-account-id="3"] [class*="dropdown"][class*="content"],
[data-account-id="3"] [class*="DropdownMenu"] {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5) !important;
}
[data-account-id="3"] [role="menu"] *,
[data-account-id="3"] [role="dialog"] *,
[data-account-id="3"] [class*="DropdownMenu"] * {
  color: rgb(var(--suritec-text)) !important;
}
[data-account-id="3"] [role="menu"] [class*="active"],
[data-account-id="3"] [role="menu"] [aria-selected="true"],
[data-account-id="3"] [role="menuitem"]:hover,
[data-account-id="3"] [role="option"]:hover {
  background-color: rgba(var(--suritec-orange), 0.12) !important;
  color: rgb(var(--suritec-orange)) !important;
}

/* Global bg-white inside account=3 → dark surface (fixes "Fall schließen" button etc.) */
[data-account-id="3"] .bg-white {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] button.bg-white,
[data-account-id="3"] button[class*="bg-white"] {
  background-color: rgb(var(--suritec-surface)) !important;
  color: rgb(var(--suritec-text)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
}
[data-account-id="3"] button.bg-white:hover {
  background-color: rgb(var(--suritec-surface-hover)) !important;
}

/* Settings sidebar items expanded chevron: keep readable */
[data-account-id="3"] aside [class*="settings"] {
  color: rgb(var(--suritec-text)) !important;
}

/* Conversation status indicator pills (resolved/snoozed/etc.) */
[data-account-id="3"] [class*="status"][class*="pill"],
[data-account-id="3"] [class*="badge"][class*="bg-"] {
  border-color: rgb(var(--suritec-line)) !important;
}

/* Settings page accent header underline */
[data-account-id="3"] [class*="settings"] h1,
[data-account-id="3"] [class*="settings"] h2,
[data-account-id="3"] [class*="settings"] h3 {
  color: rgb(var(--suritec-text-strong)) !important;
}


/* ─── iter13: nuke any visible gradient/shadow/border at sidebar footer area ─── */
[data-account-id="3"] aside > div:last-child,
[data-account-id="3"] aside [class*="border-t"][class*="border-n-weak"],
[data-account-id="3"] aside [class*="shadow-["]  {
  box-shadow: none !important;
  border-top: 0 !important;
  background: rgb(var(--suritec-black)) !important;
}
/* Catch all gradients */
[data-account-id="3"] *[class*="bg-gradient"],
[data-account-id="3"] *[style*="gradient"] {
  background-image: none !important;
  background: rgb(var(--suritec-black)) !important;
}
/* Ensure absolute-positioned overlay strips at sidebar boundaries hidden */
[data-account-id="3"] aside .absolute.inset-x-0 {
  display: none !important;
}


/* ─── iter14: account switcher dropdown specific ─── */
[data-account-id="3"] .n-dropdown-body,
[data-account-id="3"] ul.n-dropdown-body {
  background-color: rgb(var(--suritec-surface)) !important;
  border: 1px solid rgb(var(--suritec-line)) !important;
  box-shadow: 0 12px 32px rgba(0,0,0,0.6) !important;
  backdrop-filter: none !important;
}
[data-account-id="3"] .n-dropdown-item,
[data-account-id="3"] li.n-dropdown-item,
[data-account-id="3"] .n-dropdown-item > * {
  color: rgb(var(--suritec-text-strong)) !important;
}
[data-account-id="3"] .n-dropdown-item:hover,
[data-account-id="3"] .n-dropdown-item > *:hover {
  background-color: rgba(var(--suritec-orange), 0.12) !important;
}
/* Currently-active account row (the one with the checkmark) */
[data-account-id="3"] .n-dropdown-item [class*="check"],
[data-account-id="3"] .n-dropdown-item svg[class*="check"],
[data-account-id="3"] .n-dropdown-item [class*="text-green"],
[data-account-id="3"] .n-dropdown-item [class*="text-success"] {
  color: rgb(var(--suritec-orange)) !important;
}

/* Make text inside dropdown items strongly readable: heading + subtle separator */
[data-account-id="3"] .n-dropdown-item span {
  color: rgb(var(--suritec-text-strong)) !important;
}
[data-account-id="3"] .n-dropdown-item .text-n-slate-11,
[data-account-id="3"] .n-dropdown-item .text-n-slate-10 {
  color: rgb(var(--suritec-muted)) !important;
}

/* Switch account header title within popover */
[data-account-id="3"] .n-dropdown-body h3,
[data-account-id="3"] .n-dropdown-body [class*="title"],
[data-account-id="3"] .n-dropdown-body [class*="header"] {
  color: rgb(var(--suritec-muted)) !important;
  font-weight: 500;
}

/* Override the bg-n-alpha-3 globally (the dropdown body uses it) */
[data-account-id="3"] .bg-n-alpha-3 {
  background-color: rgb(var(--suritec-surface)) !important;
}

</style>
